import axios from 'axios';
import './details.css';
import './UserDetails.css';
import '../Components/modal.css';
import { EditUser } from './EditUser';
import React, {
  useEffect,
  useState,
  useContext,
  Fragment,
  useCallback,
  useRef
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BASE_URL_API, defaultIcon, STATIC_ICONS } from '../Constants';
import Spinner from '../Components/Spinner';
import { useAuth } from '../hooks/AuthProvider';
import { ChangeUserPassword } from './Users';
import { suspendUser, deleteUser, resetPassword } from '../lib/actions';
import Select from 'react-select';
import { LanguageContext } from '../context/LanguageContext';
import userTranslations from '../languages/user.json';
import Alert from '../Components/Alert';
import AppDialog from '../Components/AppDialog';
import { getErrorMessage } from '../utils/errorUtils';
import { DeleteIcon, DetailsMainIcon, VerticalDividerIcon } from '../svg/UtilsIcons';
import { AssignedDeviceIcon, EmailIcon, LocationIcon, RecentActivityIcon, ResetPasswordIcon, RoleIcon, SuspendUserIcon, TransferUserIcon, UserInformationIcon, UserProfileIcon } from '../svg/UserIcon';
import { OrganizationIcon } from '../svg/DeviceIcons';
import EllipseIcon from "../UserEllipse.svg";
import { normalizeOrganizationListRow } from '../utils/listRowIds';

export const TransferUser = ({ state, setState, refresh, canTransfer }) => {
  const { lang } = useContext(LanguageContext);
  const tRoot = userTranslations[lang];
  const t = tRoot.userDetails;
  const isRtl = ['ar', 'ur'].includes(lang);

  const [organizations, setOrganizations] = useState([]);
  const [organization, setOrganization] = useState({});

  const [alertState, setAlertState] = useState({
    open: false,
    message: '',
    type: 'info',
    position: isRtl ? 'left' : 'right'
  });

  const showAlert = (type, message) => {
    setAlertState({
      open: true,
      type,
      message,
      position: isRtl ? 'left' : 'right'
    });
  };

  useEffect(() => {
    if (!canTransfer) return;

    const fetchAllOrganizations = () => {
      axios
        .get(`${BASE_URL_API}/organization/list`)
        .then(res => {
          if (res.data && res.data.status === 'OK') {
            setOrganizations(
              Array.isArray(res.data.data)
                ? res.data.data.map(normalizeOrganizationListRow)
                : []
            );
          } else {
            showAlert('error', 'Failed to retrieve the list of organizations.');
            setOrganizations([]);
          }
        })
        .catch(err => {
          console.error('API Error:', err);
          if (err.response) {
            showAlert(
              'error',
              'An unexpected server error occurred. Please try again later.'
            );
          } else {
            showAlert('error', 'Network error. Please check your connection.');
          }
          setOrganizations([]);
        });
    };

    fetchAllOrganizations();
  }, [canTransfer]);

  const transferUser = async () => {
    if (!canTransfer) {
      showAlert('error', 'You do not have permission to transfer users.');
      return;
    }
    if (!organization.id) {
      return showAlert('error', 'Please select a destination organization.');
    }

    try {
      const res = await axios.put(
        `${BASE_URL_API}/user/transfer/${state.id}`,
        {
          organization_id: organization.id,
          organization_name: organization.name
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (res.status === 200 && res.data.status === 'OK') {
        showAlert('success', res.data.message || t.transferSuccess);
        refresh();
        setTimeout(() => {
          setState({ id: 0, open: false });
        }, 2000);
      } else {
        throw new Error('Received an unexpected response from the server.');
      }
    } catch (err) {
      console.error('User transfer failed:', err);
      const errorMessage = getErrorMessage(err);
      showAlert('error', errorMessage);
    }
  };

  if (!canTransfer) {
    return (
      <div
        id="modal"
        style={{
          direction: lang === 'ar' || lang === 'ur' ? 'rtl' : 'ltr'
        }}
      >
        <div
          id="popup"
          className="dashboard-modal"
          style={{
            animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
            fontSize:
              'calc(var(--body-font-size) * var(--font-size-multiplier))'
          }}
        >
          <div className="dashboard-header">
            <div className="dashboard-header-icon">
              <TransferUserIcon />
            </div>
            <div className="dashboard-header-text">
              <h2>
                {t.permissionDeniedTitle || 'Permission Denied'}
              </h2>
              <p>
                {t.permissionDeniedMessage ||
                  'You do not have permission to transfer users.'}
              </p>
            </div>
          </div>

          <div className="modal-divider" />

          <div className="modal-content dashboard-content" />

          <div className="modal-footer dashboard-footer">
            <button
              className="btn btn-cancel"
              onClick={() => setState({ open: false, id: 0 })}
            >
              {t.buttonClose || 'Close'}
            </button>
            <div />
          </div>
        </div>
      </div>
    );
  }

  const organizationOptions = organizations.map(orgItem => ({
    value: orgItem.id,
    label: orgItem.name,
    ...orgItem
  }));

  const selectedOrganizationOption = organizationOptions.find(
    option => option.value === organization.id
  );

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: 'rgba(8, 24, 45, 0.95)',
      borderColor: state.isFocused
        ? '#00C0C7'
        : 'rgba(255, 255, 255, 0.12)',
      boxShadow: state.isFocused
        ? '0 0 0 1px rgba(0, 192, 199, 0.6)'
        : 'none',
      '&:hover': {
        borderColor: '#00C0C7'
      },
      color: '#FFFFFF',
      fontSize:
        'calc(var(--body-font-size) * var(--font-size-multiplier))',
      minHeight: '44px',
      borderRadius: 8,
      cursor: 'pointer'
    }),
    singleValue: provided => ({
      ...provided,
      color: '#FFFFFF',
      fontSize:
        'calc(var(--body-font-size) * var(--font-size-multiplier))'
    }),
    input: provided => ({
      ...provided,
      color: '#FFFFFF',
      fontSize:
        'calc(var(--body-font-size) * var(--font-size-multiplier))'
    }),
    placeholder: provided => ({
      ...provided,
      color: 'rgba(200, 220, 255, 0.4)',
      fontSize:
        'calc(var(--body-font-size) * var(--font-size-multiplier))'
    }),
    menu: provided => ({
      ...provided,
      backgroundColor: '#071128',
      border: '1px solid rgba(255, 255, 255, 0.12)',
      borderRadius: 8,
      overflow: 'hidden',
      zIndex: 9999
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? '#00C0C7'
        : state.isFocused
        ? 'rgba(13, 42, 77, 0.9)'
        : '#071128',
      color: state.isSelected ? '#021020' : '#FFFFFF',
      fontSize:
        'calc(var(--body-font-size) * var(--font-size-multiplier))',
      '&:active': {
        backgroundColor: '#00A0AF'
      },
      cursor: 'pointer'
    }),
    dropdownIndicator: provided => ({
      ...provided,
      color: '#FFFFFF',
      paddingRight: 12,
      '&:hover': {
        color: '#00C0C7'
      }
    }),
    clearIndicator: provided => ({
      ...provided,
      color: '#FFFFFF',
      '&:hover': {
        color: '#00C0C7'
      }
    }),
    indicatorSeparator: () => ({
      display: 'none'
    })
  };

  return (
    <Fragment>
      <Alert
        open={alertState.open}
        close={() =>
          setAlertState({ ...alertState, open: false })
        }
        message={alertState.message}
        type={alertState.type}
        position={alertState.position}
      />
      <div
        id="modal"
        style={{
          direction: lang === 'ar' || lang === 'ur' ? 'rtl' : 'ltr'
        }}
      >
        <div
          id="popup"
          className="dashboard-modal"
          style={{
            animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
            fontSize:
              'calc(var(--body-font-size) * var(--font-size-multiplier))'
          }}
        >
          <div className="dashboard-header">
            <div className="dashboard-header-icon">
              <TransferUserIcon />
            </div>
            <div className="dashboard-header-text">
              <h2>{t.transferTitle}</h2>
              <p>
                {t.transferDesc ||
                  'Select a destination organization to transfer this user.'}
              </p>
            </div>
          </div>

          <div className="modal-divider" />

          <div className="modal-content dashboard-content">
            <div className="form-section">
              <div className="form-section-title">
                <span className="form-section-icon">
                  <TransferUserIcon />
                </span>
                <span className="form-section-title-text">
                  {t.selectOrgLabel || 'Destination organization'}
                </span>
              </div>

              <div className="form-item">
                <Select
                  id="organization"
                  options={organizationOptions}
                  value={selectedOrganizationOption}
                  onChange={selectedOption =>
                    setOrganization(selectedOption || {})
                  }
                  styles={customStyles}
                  placeholder={
                    t.selectOrgPlaceholder ||
                    'Select destination organization'
                  }
                  isClearable
                />
              </div>
            </div>
          </div>

          <div className="modal-footer dashboard-footer">
            <button
              className="btn btn-cancel"
              onClick={() => setState({ open: false, id: 0 })}
            >
              {t.buttonCancel}
            </button>
            <button
              className="btn btn-apply dashboard-primary-btn"
              onClick={transferUser}
            >
              {t.buttonTransfer}
            </button>
          </div>
        </div>
      </div>
    </Fragment>
  );
};



export const UserDetails = () => {
  const { lang } = useContext(LanguageContext);
  const tRoot = userTranslations[lang];
  const t = tRoot.userDetails;
  const isRtl = ['ar', 'ur'].includes(lang);
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, setUser, permissions } = useAuth();

  const canView = permissions['view_users'];
  const canEdit = permissions['edit_users'];
  const canSuspend = permissions['suspend_users'];
  const canTransfer = permissions['transfer_users'];
  const canChangePassword = permissions['change_user_password'];
  const canDelete = permissions['delete_users'];

  const [details, setDetails] = useState({ data: {}, loading: true });
  const [editState, setEditState] = useState({
    open: false,
    id: id
  });
  const [changePassword, setChangePassword] = useState({
    open: false,
    userId: 0
  });
  const [transfer, setTransfer] = useState({ id: 0, open: false });
  const [alertState, setAlertState] = useState({
    open: false,
    message: '',
    type: 'info',
    position: isRtl ? 'left' : 'right'
  });
  const showAlert = useCallback(
    (type, message) => {
      setAlertState({
        open: true,
        type,
        message,
        position: isRtl ? 'left' : 'right'
      });
    },
    [isRtl]
  );


  const resolverRef = useRef(null);
  const [dialog, setDialog] = useState({
    open: false,
    type: 'alert', // 'alert' | 'confirm'
    variant: 'info', // 'info' | 'danger' | 'error'
    icon: 'alert', // 'alert' | 'delete'
    title: '',
    message: '',
    confirmText: 'Ok',
    cancelText: 'Cancel',
    showCancel: false
  });

  const closeDialog = useCallback((result = false) => {
    setDialog(d => ({ ...d, open: false }));
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  }, []);

  const alert = useCallback(
    opts =>
      new Promise(resolve => {
        resolverRef.current = resolve;

        setDialog({
          open: true,
          type: opts?.type || 'alert',
          variant: opts?.variant || 'info',
          icon: opts?.icon || 'alert',
          title: opts?.title || '',
          message: opts?.message || '',
          confirmText: opts?.confirmText || 'Ok',
          cancelText: opts?.cancelText || 'Cancel',
          showCancel: Boolean(opts?.showCancel)
        });
      }),
    []
  );


  const fetchUser = useCallback(
    async () => {
      if (!canView) {
        setDetails({ data: null, loading: false });
        return;
      }
      setDetails(prev => ({ ...prev, loading: true }));
      try {
        const res = await axios.get(`${BASE_URL_API}/user/get/${id}`);
        if (res.data && res.data.status === 'OK') {
          setDetails({ data: res.data.data, loading: false });
        } else {
          throw new Error('Received an invalid response from the server.');
        }
      } catch (err) {
        console.error('Failed to fetch user:', err);
        const errorMessage = getErrorMessage(err);
        showAlert('error', errorMessage);
        setDetails({ data: null, loading: false });
      }
    },
    [id, canView]
  );

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const isSelf = parseInt(id, 10) === user.id;
  const targetRole = (details.data.role || '').toLowerCase();
  const isProtectedAccount = ['owner', 'super'].includes(targetRole);
  const canEditThisUser = Boolean(canEdit) && (!isProtectedAccount || isSelf);
  const canChangePasswordThisUser = Boolean(canChangePassword) && (!isProtectedAccount || isSelf);
  const hasUserIcon =
  details.data.icon && typeof details.data.icon === 'string' && details.data.icon.length > 0;


  if (!canView) {
    return <div>{t.noPermission}</div>;
  }

  if (details.loading) {
    return <Spinner />;
  }

  if (!details.data?.id) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {t.noData}
      </div>
    );
  }

  const handleDeleteUser = () => {
    if (!canDelete || isProtectedAccount || isSelf) return;
    deleteUser(details.data.id, () => navigate('/administration/users'), alert);
  };

  return (
    <div className="configuration user-page">
      <Alert
        open={alertState.open}
        close={() => setAlertState({ ...alertState, open: false })}
        message={alertState.message}
        type={alertState.type}
        position={alertState.position}
      />


      <AppDialog
        open={dialog.open}
        type={dialog.type}
        variant={dialog.variant}
        icon={dialog.icon}
        title={dialog.title}
        message={dialog.message}
        confirmText={dialog.confirmText}
        cancelText={dialog.cancelText}
        showCancel={dialog.showCancel}
        onConfirm={() => closeDialog(true)}
        onCancel={() => closeDialog(false)}
        onClose={() => closeDialog(false)}
      />


      {editState.open && canEditThisUser && (
        <EditUser
          bg={'var(--cell-background)'}
          fg={'var(--cell-color)'}
          ft={'var(--header-background)'}
          btnPrimary={'rgb(219, 245, 236)'}
          btnSecondary={'var(--accent)'}
          state={editState}
          setState={setEditState}
          refresh={fetchUser}
        />
      )}
      {transfer.open && canTransfer && (
        <TransferUser
          state={transfer}
          setState={setTransfer}
          refresh={fetchUser}
          canTransfer={canTransfer}
        />
      )}
      {changePassword.open && canChangePasswordThisUser && (
        <ChangeUserPassword
          state={changePassword}
          setState={setChangePassword}
          canChangePassword={canChangePassword}
        />
      )}

      <div className="asset-top-bar">
        <button
          className="asset-back-btn"
          onClick={() => navigate('/administration/users')}
        >
          <span className="asset-back-icon">←</span>
          <span>{t.backLabel || 'Back'}</span>
        </button>

        <div className="asset-top-actions">
          {canEditThisUser && (
            <button
              className="asset-edit-main-btn"
              disabled={!canEditThisUser}
              onClick={() => {
                if (!canEditThisUser) {
                  showAlert('error', t.noPermission || 'You do not have permission to edit this user.');
                  return;
                }
                setEditState({ ...editState, open: true });
              }}
            >
              {t.editUser || 'Edit'}
            </button>
          )}
        </div>
      </div>

      <div className="asset-hero-card user-hero-card">
  <div className="asset-hero-left">
<div className="asset-hero-icon user-hero-icon user-avatar-icon">
          {hasUserIcon ? (
            <img
              alt="user"
              src={`${STATIC_ICONS}${details.data.icon}`}
              className="user-avatar-photo"
            />
          ) : (
            <>
              <img
                src={EllipseIcon}
                alt="profile-bg"
                className="user-avatar-bg"
              />
              <UserProfileIcon className="user-avatar-svg" />
            </>
          )}
        </div>


    <div className="asset-hero-text">
      <h1>{details.data.name}</h1>

      <div className="asset-hero-meta">
        {details.data.organization_name && (
          <div
            className="user-hero-org"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              color: '#01ABB4',
              fontSize:
                'calc(var(--ui-font-size) * var(--font-size-multiplier))',
            }}
          >
            <OrganizationIcon color={'#01ABB4'} />
            <span>{details.data.organization_name}</span>
          </div>
        )}

        {(canDelete && !isProtectedAccount && !isSelf) && (
          <span className="details-inline-divider">
              <VerticalDividerIcon />
          </span>
        )}

        {(canDelete && !isProtectedAccount && !isSelf) && (
          <button
            type="button"
            className="user-hero-delete-btn"
            onClick={handleDeleteUser}
            style={{
              border: 'none',
              background: 'transparent',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
            }}
          >
            <DeleteIcon color="rgb(236, 69, 78)" />
          </button>
        )}
      </div>
    </div>
  </div>
</div>


      <div className="user-main-panel">
        <div className="user-action-row">
          <button
            type="button"
            className="user-action-card user-action-reset"
            disabled={!canChangePasswordThisUser}
            onClick={() =>
              setChangePassword({
                open: true,
                userId: details.data.id
              })
            }
          >
            <div className="user-action-card-header">
              <div className="user-action-icon user-action-icon-reset">
                <ResetPasswordIcon color={'var(--accent)'} />
              </div>
              <span className="user-action-title">
                {tRoot.actions.resetPassword}
              </span>
            </div>
            <span className="user-action-subtitle">
              {t.resetPasswordSubtitle || 'Modify user details'}
            </span>
          </button>

          <button
            type="button"
            className="user-action-card user-action-transfer"
            disabled={!canTransfer || isProtectedAccount || isSelf}
            onClick={() => setTransfer({ id: details.data.id, open: true })}
          >
            <div className="user-action-card-header">
              <div className="user-action-icon user-action-transfer">
                <TransferUserIcon style={{ color: 'var(--accent)' }} />
              </div>
              <span className="user-action-title">
                {tRoot.actions.transferUser}
              </span>
            </div>
            <span className="user-action-subtitle">
              {t.transferSubtitle || 'Transfer user'}
            </span>
          </button>

          <button
            type="button"
            className="user-action-card user-action-suspend"
            disabled={!canSuspend || isProtectedAccount || isSelf}
            onClick={() =>
              suspendUser(
                details.data.id,
                details.data.id === user.id,
                () => setUser({ ...user, status: 'Suspended' }),
                fetchUser,
                alert
              )
            }
          >
            <div className="user-action-card-header">
              <div className="user-action-icon user-action-icon-suspend">
                <SuspendUserIcon style={{ color: 'var(--accent)' }} />
              </div>
              <span className="user-action-title">
                {tRoot.actions.suspendUser}
              </span>
            </div>
            <span className="user-action-subtitle">
              {t.suspendSubtitle || 'Suspend user access'}
            </span>
          </button>
        </div>

        <div className="user-main-grid">
<section className="user-section-card user-info-card">
            <header className="user-section-header">
              <div className="user-section-icon">
                <UserInformationIcon color={'var(--accent)'} />
              </div>
              <div className="user-section-title-block">
                <h2>{t.userInfoTitle || 'User Information'}</h2>
              </div>
            </header>

            <div className="user-info-grid">
              <div className="user-info-col">
                {details.data.status && (
                  <div className="user-info-field">
                    <div className="user-info-label">
                      {t.fieldStatus || 'Status'}
                    </div>
                    <div className="user-info-pill user-info-pill-status">
                      <span className="user-status-dot" />
                      <span className="user-info-text">
                        {details.data.status}
                      </span>
                    </div>
                  </div>
                )}

                {details.data.role && (
                  <div className="user-info-field">
                    <div className="user-info-label">
                      {t.fieldRole || 'Role'}
                    </div>
                    <div className="user-info-pill">
                      <span className="user-info-pill-icon">
                        <RoleIcon color={'var(--accent)'} />
                      </span>
                      <span className="user-info-text">
                        {details.data.role}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="user-info-col">
                {details.data.email && (
                  <div className="user-info-field">
                    <div className="user-info-label">
                      {t.fieldEmail || 'Email address'}
                    </div>
                    <div className="user-info-pill">
                      <span className="user-info-pill-icon">
                        <EmailIcon color={'var(--accent)'} />
                      </span>
                      <span className="user-info-text user-info-text-email">
                        {details.data.email}
                      </span>

                      {details.data.badge_value && (
                        <span className="user-info-badge">
                          {details.data.badge_value}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <div className="user-info-field">
                  <div className="user-info-label">
                    {t.fieldDeviceCount || 'Assigned devices'}
                  </div>
                  <div className="user-info-pill">
                    <span className="user-info-pill-icon">
                      <AssignedDeviceIcon color={'var(--accent)'} />
                    </span>
                    <span className="user-info-text">
                      {details.data.device_count}{' '}
                      {t.devicesLabel || 'devices'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="user-info-location-row">
              <div className="user-info-field user-info-field-full">
                <div className="user-info-label">
                  {t.fieldLocation || 'Location'}
                </div>
                <div className="user-info-pill">
                  <span className="user-info-pill-icon">
                    <LocationIcon color={'var(--accent)'} />
                  </span>
                  <span className="user-info-text">
                    {details.data.location ||
                      details.data.country ||
                      '-'}
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* <section className="user-section-card user-activity-card">
            <header className="user-section-header user-section-header-purple">
              <div className="user-section-icon  user-section-icon-recent">
                <RecentActivityIcon />
              </div>
              <div className="user-section-title-block">
                <h2>{t.recentActivityTitle || 'Recent Activity'}</h2>
              </div>
            </header>

            <div className="user-activity-list">
              {[1, 2, 3, 4, 5].map(idx => (
                <div key={idx} className="user-activity-item">
                  <div className="user-activity-main">
                    {t[`activityTitle${idx}`] ||
                      `Modified device settings`}
                  </div>
                  <div className="user-activity-meta">
                    <span className="user-activity-device">
                      {t[`activityDevice${idx}`] || 'Device / Asset name'}
                    </span>
                    <span className="user-activity-time">
                      {t[`activityTime${idx}`] || '1 hour ago'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section> */}
        </div>
      </div>
    </div>
  );
};
